/**
 * One row of the version timeline in the editor's version chip (UX-05), extracted for mockup v3 so
 * each phase fills its own slot: `note` is P4's (the version note), `cost` is P7's (the cost of the
 * version) and `actions` stays the chip's (Ver, Comparar, Usar esta versión). A slot left empty
 * draws nothing; `actions` is `null` on the current version.
 */

import type { ReactElement, ReactNode } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Badge } from "../../../shared/ui/atoms/Badge";
import { dayText } from "../../../shared/ui/dayText";
import styles from "./VersionTimelineRow.module.css";

export interface VersionTimelineRowProps {
  readonly sequenceNumber: number;
  readonly createdAt: string;
  readonly isCurrent: boolean;
  /** Whether the canvas shows this version. */
  readonly shown: boolean;
  /** P4: the version's note. */
  readonly note?: ReactNode;
  /** P7: the version's cost. */
  readonly cost?: ReactNode;
  /** The row's buttons, or `null` for none. */
  readonly actions: ReactNode;
}

export function VersionTimelineRow({
  sequenceNumber,
  createdAt,
  isCurrent,
  shown,
  note,
  cost,
  actions,
}: VersionTimelineRowProps): ReactElement {
  const { t } = useTranslation();
  return (
    <li className={styles.row} data-shown={shown ? "true" : undefined}>
      <h3 className={styles.name}>{t("editor.versions.chip", { n: sequenceNumber })}</h3>
      <div className={styles.body}>
        <div className={styles.what}>
          <time dateTime={createdAt}>{dayText(createdAt)}</time>
          {isCurrent ? <Badge tone="info">{t("versions.current")}</Badge> : null}
        </div>
        {note}
        {cost}
        {actions === null || actions === undefined ? null : <div className={styles.actions}>{actions}</div>}
      </div>
    </li>
  );
}
